import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { HiOutlineSparkles } from 'react-icons/hi2';
import styles from './SideDrawer.module.css';
import type { SignalsOnboardingStep } from './signalsOnboardingSteps';

type SignalsOnboardingTourProps = {
  steps: SignalsOnboardingStep[];
  stepIndex: number;
  isOpen: boolean;
  heading?: string;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  onDone: () => void;
  onClose: () => void;
};

const VIEWPORT_PADDING = 12;
const POPOVER_FALLBACK_WIDTH = 380;

const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

const SignalsOnboardingTour = ({
  steps,
  stepIndex,
  isOpen,
  heading,
  onNext,
  onBack,
  onSkip,
  onDone,
  onClose,
}: SignalsOnboardingTourProps) => {
  const step = steps[stepIndex];
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const anchorElement = useMemo(() => {
    if (!isOpen || !step?.anchor) {
      return null;
    }
    const byTestId = document.querySelector(`[data-testid="${step.anchor}"]`) as HTMLElement | null;
    if (byTestId) {
      return byTestId;
    }
    return document.querySelector(`[data-onboard="${step.anchor}"]`) as HTMLElement | null;
  }, [isOpen, step?.anchor]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key === 'ArrowRight') {
        onNext();
      }
      if (event.key === 'ArrowLeft') {
        onBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onBack, onClose, onNext]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const popoverRect = popoverRef.current?.getBoundingClientRect();
      const popoverWidth = popoverRect?.width ?? POPOVER_FALLBACK_WIDTH;
      const popoverHeight = popoverRect?.height ?? 220;
      const maxLeft = window.innerWidth - popoverWidth - VIEWPORT_PADDING;
      const maxTop = window.innerHeight - popoverHeight - VIEWPORT_PADDING;

      if (!anchorElement) {
        const left = clamp((window.innerWidth - popoverWidth) / 2, VIEWPORT_PADDING, maxLeft);
        const top = clamp((window.innerHeight - popoverHeight) / 2, VIEWPORT_PADDING, maxTop);
        setPosition({ top, left });
        return;
      }

      const anchorRect = anchorElement.getBoundingClientRect();
      let left = anchorRect.left;
      let top = anchorRect.bottom + VIEWPORT_PADDING;

      if (left > maxLeft) {
        left = clamp(left, VIEWPORT_PADDING, maxLeft);
      }

      if (top > maxTop) {
        const aboveTop = anchorRect.top - popoverHeight - VIEWPORT_PADDING;
        top = aboveTop >= VIEWPORT_PADDING ? aboveTop : clamp(top, VIEWPORT_PADDING, maxTop);
      }

      setPosition({ top, left });
    };

    if (anchorElement) {
      anchorElement.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
    }

    let rafId = window.requestAnimationFrame(updatePosition);
    const handleResize = () => {
      window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(updatePosition);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [anchorElement, isOpen, stepIndex]);

  if (!isOpen || !step || !position) {
    return null;
  }

  return (
    <div
      ref={popoverRef}
      className={styles.signalsTourPopover}
      style={{ top: position.top, left: position.left }}
      role="dialog"
      aria-live="polite"
    >
      <div className={styles.signalsTourHeading}>
        <HiOutlineSparkles className={styles.sparklesIcon} />
        {heading || 'How to use signals'}
      </div>
      <div className={styles.signalsTourStep}>
        Step {stepIndex + 1} of {steps.length}
      </div>
      <div className={styles.signalsTourTitle}>{step.title}</div>
      {step.body ? <div className={styles.signalsTourBody}>{step.body}</div> : null}
      {step.example && <div className={styles.signalsTourExample}>{step.example}</div>}
      {step.sections?.length ? (
        <div className={styles.signalsTourSections}>
          {step.sections.map((section) => (
            <div key={section.title} className={styles.signalsTourSection}>
              <div className={styles.signalsTourSectionTitle}>{section.title}</div>
              <ul className={styles.signalsTourSectionList}>
                {section.items.map((item) => (
                  <li key={item} className={styles.signalsTourSectionItem}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
      {step.bulletsTitle && (
        <div className={styles.signalsTourBulletsTitle}>{step.bulletsTitle}</div>
      )}
      {step.bullets?.length ? (
        <ul className={styles.signalsTourBullets}>
          {step.bullets.map((item) => (
            <li key={item} className={styles.signalsTourBullet}>
              {item}
            </li>
          ))}
        </ul>
      ) : null}
      {step.closing && <div className={styles.signalsTourClosing}>{step.closing}</div>}
      {step.footerHint && <div className={styles.signalsTourFooter}>{step.footerHint}</div>}
      {step.boldLine && <div className={styles.signalsTourBold}>{step.boldLine}</div>}
      <div className={styles.signalsTourActions}>
        <button
          type="button"
          className={styles.signalsTourSkip}
          onClick={onSkip}
        >
          Skip
        </button>
        <div className={styles.signalsTourActionGroup}>
          <button
            type="button"
            className={styles.signalsTourBack}
            onClick={onBack}
            disabled={stepIndex === 0}
          >
            <FaChevronLeft className={styles.tourIcon} />
            Back
          </button>
          <button
            type="button"
            className={styles.signalsTourNext}
            onClick={onNext}
          >
            Next
            <FaChevronRight className={styles.tourIcon} />
          </button>
          <button
            type="button"
            className={styles.signalsTourDone}
            onClick={onDone}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignalsOnboardingTour;
